import { useToast } from '@chakra-ui/react';
import React from 'react';
import { TOAST_CONFIG, WEBSOCKET_CONFIG } from './config';
import { ConnectionConfig, ConnectionState, ReadyState, UseWebSocketConnectionReturn } from './types';

export function useWebSocketConnection(uri: string, options: Partial<ConnectionConfig> = {}): UseWebSocketConnectionReturn {
  const toast = useToast({ position: TOAST_CONFIG.position });

  const transport = React.useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const connectionTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const [connectionState, setConnectionState] = React.useState<ConnectionState>({
    readyState: ReadyState.UNINSTANTIATED,
    retries: 0,
  });

  // Computed state properties
  const isConnected = connectionState.readyState === ReadyState.OPEN;
  const config: ConnectionConfig = {
    uri,
    ...WEBSOCKET_CONFIG,
    ...options,
  };

  // Connection event handlers
  const onOpen = React.useCallback(function (this: WebSocket) {
    console.log('WebSocket connected');

    // Clear connection timeout
    if (connectionTimeoutRef.current) {
      clearTimeout(connectionTimeoutRef.current);
      connectionTimeoutRef.current = null;
    }

    setConnectionState((prev) => ({
      ...prev,
      readyState: ReadyState.OPEN,
      retries: 0,
      lastConnected: new Date(),
      lastError: undefined,
    }));
  }, []);

  const onError = React.useCallback(function (this: WebSocket, event: Event) {
    console.log('WebSocket error', event);

    setConnectionState((prev) => ({
      ...prev,
      lastError: 'Connection error occurred',
    }));
  }, []);

  const onClose = React.useCallback(
    function (this: WebSocket, event: CloseEvent) {
      console.log('WebSocket closed', event);

      // Clear connection timeout
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
        connectionTimeoutRef.current = null;
      }

      // Remove event listeners
      this.removeEventListener('open', onOpen);
      this.removeEventListener('error', onError);
      this.removeEventListener('close', onClose);

      // Update Connection State
      setConnectionState((prev) => ({
        ...prev,
        retries: event.code > 1000 ? prev.retries + 1 : config.maxRetries,
        readyState: ReadyState.CLOSED,
      }));
    },
    [connectionState.retries, config.maxRetries, config.reconnectDelay, toast],
  );

  // Connection management functions
  const connect = React.useCallback(() => {
    // Prevent multiple connection attempts
    if (![ReadyState.UNINSTANTIATED, ReadyState.CLOSED].includes(connectionState.readyState)) {
      return;
    }

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (connectionTimeoutRef.current) {
      clearTimeout(connectionTimeoutRef.current);
      connectionTimeoutRef.current = null;
    }

    console.log(`Attempting to connect to ${config.uri}`);

    setConnectionState((prev) => ({
      ...prev,
      readyState: ReadyState.CONNECTING,
    }));

    try {
      // Initialize websocket
      const websocket = new WebSocket(config.uri);
      websocket.addEventListener('open', onOpen);
      websocket.addEventListener('error', onError);
      websocket.addEventListener('close', onClose);
      transport.current = websocket;

      // Set connection timeout
      connectionTimeoutRef.current = setTimeout(() => {
        if (websocket.readyState !== ReadyState.OPEN) {
          console.warn('Connection timeout, closing socket');
          websocket.close();
        }
      }, config.connectionTimeout);
    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
      setConnectionState((prev) => ({
        ...prev,
        readyState: ReadyState.CLOSED,
        lastError: error instanceof Error ? error.message : 'Unknown error',
      }));
    }
  }, [config.uri, config.connectionTimeout, connectionState.readyState, onOpen, onError, onClose]);

  const disconnect = React.useCallback(() => {
    // Clear any pending timeouts
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (connectionTimeoutRef.current) {
      clearTimeout(connectionTimeoutRef.current);
      connectionTimeoutRef.current = null;
    }

    if (transport.current && transport.current.readyState === ReadyState.OPEN) {
      console.log('Disconnecting WebSocket');
      transport.current.close(1000, 'User initiated disconnect');
    }
    transport.current = null;

    setConnectionState((prev) => ({
      ...prev,
      readyState: ReadyState.CLOSING,
    }));
  }, []);

  const reset = React.useCallback(() => {
    console.log('Resetting WebSocket connection');
    disconnect();
    setConnectionState({
      readyState: ReadyState.UNINSTANTIATED,
      retries: 0,
    });
  }, [disconnect]);

  // Effects
  // -- disconnect on unmount
  React.useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  // -- Schedule reconnection
  React.useEffect(() => {
    if (reconnectTimeoutRef.current || connectionState.readyState !== ReadyState.CLOSED) return;

    if (connectionState.retries < config.maxRetries) {
      console.warn(`Socket closed. Reconnecting in ${config.reconnectDelay}ms (attempt ${connectionState.retries}/${config.maxRetries})`);
      toast({
        duration: TOAST_CONFIG.duration.warning,
        isClosable: true,
        title: 'Reconnecting in 5 seconds',
        description: 'Connection to the production server is lost.',
        status: 'warning',
      });

      reconnectTimeoutRef.current = setTimeout(() => {
        connect();
      }, config.reconnectDelay);
    } else if (connectionState.retries === config.maxRetries) {
      console.error('Socket closed. Max reconnection attempts reached or connection was closed intentionally.');
      setConnectionState((prev) => ({
        ...prev,
        readyState: ReadyState.MAX_RETRIES_REACHED,
      }));
    }
  }, [connectionState.readyState, connectionState.retries, connect, config.reconnectDelay, toast, config.maxRetries]);

  return {
    transport: isConnected ? transport.current : null,
    readyState: connectionState.readyState,
    connect,
    disconnect,
    reset,
  };
}
